import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  scheduledMessagesApi,
  CreateScheduledMessageRequest,
  UpdateScheduledMessageRequest,
} from "api/scheduledmessages/scheduledmessagesApi";

const SCHEDULED_MESSAGES_QUERY_KEY = "scheduledMessages";

export const useScheduledMessages = (
  skip: number,
  take: number,
  searchPhrase: string = "",
  filter: string = "",
  orderBy: string = "",
) => {
  const query = useQuery({
    queryKey: [SCHEDULED_MESSAGES_QUERY_KEY, skip, take, searchPhrase, filter, orderBy],
    queryFn: () => scheduledMessagesApi.fetch(skip, take, searchPhrase, filter, orderBy),
    select: (data) => data.data,
  });

  return {
    scheduledMessages: query.data?.data || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    invalidate: () => query.refetch(),
  };
};

export const useScheduledMessage = (id: number | null) => {
  const query = useQuery({
    queryKey: [SCHEDULED_MESSAGES_QUERY_KEY, id],
    queryFn: () => scheduledMessagesApi.fetchById(id!),
    enabled: !!id,
    select: (data) => data.data,
  });

  return {
    scheduledMessage: query.data,
    isLoading: query.isLoading,
    error: query.error,
    invalidate: () => query.refetch(),
  };
};

export const useScheduledMessageRecipients = (id: number | null) => {
  const query = useQuery({
    queryKey: [SCHEDULED_MESSAGES_QUERY_KEY, id, "recipients"],
    queryFn: () => scheduledMessagesApi.fetchRecipients(id!),
    enabled: !!id,
    select: (data) => data.data,
  });

  return {
    recipients: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    invalidate: () => query.refetch(),
  };
};

export const useCreateScheduledMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateScheduledMessageRequest) => scheduledMessagesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY] });
    },
  });
};

export const useUpdateScheduledMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateScheduledMessageRequest }) =>
      scheduledMessagesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY, id] });
    },
  });
};

export const useScheduleMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, deliveryTime }: { id: number; deliveryTime: string }) =>
      scheduledMessagesApi.schedule(id, deliveryTime),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY, id] });
    },
  });
};

export const useSendMessageImmediately = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => scheduledMessagesApi.sendImmediately(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY, id] });
    },
  });
};

export const useCancelScheduledMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => scheduledMessagesApi.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY, id] });
    },
  });
};

export const useDeleteScheduledMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => scheduledMessagesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_MESSAGES_QUERY_KEY] });
    },
  });
};
